import { Button } from 'react-bootstrap';
import React, { useState } from 'react';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { openUrlInNewTab } from '../../util/window-utils';
import useApi from '../../api/backendApiContext';
import { useMutation } from '@tanstack/react-query';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { v4 as uuidv4 } from 'uuid';
import { useNotification } from '../../notification/notificationsContext';
import { useFetchedBillingInvoices } from '../../billing/hooks/useFetchedBillingInvoices';
import { INVOICE_TYPE_ISSUED, IssuedInvoice } from '../../billing/types';
import { isInvoicePaid, isInvoicePayable } from '../../billing/utils';
import { FinancialTemplatePayInvoicesModal } from './FinancialTemplatePayInvoicesModal';
import { FinancialTemplateInstructionsModal } from './FinancialTemplateInstructionsModal';

export const DownloadBusinessPlanFinancialTemplateButton = () => {
  const analytics = useAnalytics();
  const { businessPlanApi } = useApi();
  const { notifyError } = useNotification();
  const {
    query: { data: invoices },
  } = useFetchedBillingInvoices({ refetchOnWindowFocus: 'always' });

  const unpaidInvoices = (invoices ?? [])
    .filter((x) => x.type === INVOICE_TYPE_ISSUED)
    .filter(isInvoicePayable) as IssuedInvoice[];
  const paidInvoices = (invoices ?? [])
    .filter((x) => x.type === INVOICE_TYPE_ISSUED)
    .filter(isInvoicePaid) as IssuedInvoice[];

  const showPayInvoicesModal = !!invoices && unpaidInvoices.length > 0 && paidInvoices.length === 0;

  const [payInvoicesModalKey, setPayInvoicesModalKey] = useState<number | null>(null);

  const [successModalDialogKey, setSuccessModalDialogKey] = useState<string | null>(null);
  const [presignedLink, setPresignedLink] = useState<string | null>(null);

  const fetchUrlAndOpenInNewTab = async () => {
    const financialPartTemplate = await businessPlanApi.getFinancialPartTemplate();
    logger.info('Redirecting to s3 business plan file');
    analyticsTrack(analytics, AnalyticsEvent.BUSINESS_PLAN_FINANCIAL_ANALYSIS_TEMPLATE_REQUESTED);

    if (!financialPartTemplate.url) {
      logger.error('Failed to get business plan download url');
      return;
    }
    setPresignedLink(financialPartTemplate.url);
    setSuccessModalDialogKey(uuidv4());
    openUrlInNewTab(financialPartTemplate.url, {
      analytics,
      analyticsEvent: AnalyticsEvent.BUSINESS_PLAN_FINANCIAL_ANALYSIS_TEMPLATE_DOWNLOADED,
    });
  };

  const { isLoading: presignedUrlLoading, mutate: urlFetchMutation } = useMutation(
    fetchUrlAndOpenInNewTab,
    {
      onError: () => {
        analyticsTrack(analytics, AnalyticsEvent.BUSINESS_PLAN_FINANCIAL_ANALYSIS_TEMPLATE_FAILED);
        notifyError({
          notificationMsg:
            'Something went wrong while requesting the financial analysis template. Please contact support.',
        });
      },
    }
  );

  const onDownloadClick = () => {
    if (!invoices) return;
    if (presignedUrlLoading) {
      return;
    }
    if (showPayInvoicesModal) {
      setPayInvoicesModalKey(new Date().valueOf());
      return;
    }
    urlFetchMutation();
  };

  return (
    <>
      <Button
        type="button"
        className="button-apply survey-section__save-button"
        disabled={presignedUrlLoading}
        onClick={onDownloadClick}
      >
        Download DIY template
      </Button>

      {payInvoicesModalKey && showPayInvoicesModal && (
        <FinancialTemplatePayInvoicesModal
          onClose={() => setPayInvoicesModalKey(null)}
          key={payInvoicesModalKey}
          invoices={unpaidInvoices}
        />
      )}

      {presignedLink && successModalDialogKey && (
        <FinancialTemplateInstructionsModal
          presignedLink={presignedLink}
          onClose={() => setSuccessModalDialogKey(null)}
        />
      )}
    </>
  );
};
